import React, { useEffect, useState } from "react";
import "./Invoice.css";
import {  ToastContainer } from "react-toastify";
import useApi from "../../../../../utils/api-manager/Helper/useApi";
import { DatePicker } from "antd";
import LoadingOverlay from "../../../../common/LoadingBar.js";
import loader from "../../../../../assets/Image/Loader2.gif";
import PropTypes from 'prop-types';
import useSwal from "../../../../common/ReusableComponent/useSwal";
import { useToastHandler } from "../../../../common/ToastHandler";
import useFormattingUtils from "../../../../../context/useFormattingUtils";

const PayInvoiceTable = ({ onEdit, reloadData }) => {
  const { Delete, GetPaginatedData } = useApi();
  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [sortOrder, setSortOrder] = useState({ column: "", direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const invoicesPerPage = 10;
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("");
  const [vendors, setVendors] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [originalInvoices , setOriginalInvoices] = useState([]) 
  const { confirmDelete } = useSwal();
  const {handleToast} = useToastHandler()

  const { formatDate } = useFormattingUtils();

  const fetchInvoices = async () => {
    setLoading(true);

    try {
      const result = await GetPaginatedData("invoiceData", {
        page: currentPage,
        search: searchQuery,
        queryParams: {
          status: selectedStatus !== "All" ? selectedStatus : "",
          vendor: selectedVendor,
          start_date: dateRange && dateRange.length === 2 ? dateRange[0] : "",
          end_date: dateRange && dateRange.length === 2 ? dateRange[1] : "",
        }
      });

      const filteredData = result.data.filter((invoice) => !invoice.is_deleted);
      setInvoices(filteredData);
      setTotalCount(result.totalCount || 0);
      setTotalPages(Math.ceil((result.totalCount || 0) / invoicesPerPage));

      // Build unique vendors as array of { id, name }
      const vendorMap = {};
      filteredData.forEach((invoice) => {
        if (invoice.vendor_department && invoice.vendor_department_name) {
          vendorMap[invoice.vendor_department] = invoice.vendor_department_name;
        }
      });
      const uniqueVendors = Object.entries(vendorMap).map(([id, name]) => ({ id, name }));
      setVendors(uniqueVendors);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching invoices:", error);
      handleToast({
        type: "ERROR",
        message: "An error occurred while fetching invoices. Please try again.",
      });
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 600);
    return () => clearTimeout(handler);
  }, [searchQuery]);
  useEffect(() => {
    fetchInvoices();
  }, [reloadData, currentPage, searchQuery, selectedStatus, selectedVendor, dateRange, sortOrder]);

  const applyFiltersAndSearch = () => {

    setCurrentPage(1);
  };

  useEffect(() => {
    applyFiltersAndSearch();
  }, [searchQuery, selectedStatus, selectedVendor, dateRange]);

  const handleSort = (column) => {
    const direction =
      sortOrder.column === column && sortOrder.direction === "asc"
        ? "desc"
        : "asc";
    setSortOrder({ column, direction });

  };

  const currentInvoices = invoices;
  const totalInvoices = totalCount; 

  const handleDelete = async (id) => {
    try {
      const result = await confirmDelete()

      if (!result.isConfirmed) return; 

      await Delete(`invoiceData`, id)
        .then((resp) => {

          const updatedOriginalInvoices = originalInvoices.filter(
            (invoice) => invoice.id !== id
          );
          const updatedInvoices = invoices.filter(
            (invoice) => invoice.id !== id
          );

          setOriginalInvoices(updatedOriginalInvoices);
          setInvoices(updatedInvoices);
          onEdit(null);

          handleToast({
            type:"SUCCESS",
            message:"Invoice successfully deleted!"
          })
        })
        .catch((error) => {
          if (error.response) {
            handleToast({
              type:"ERROR",
              message:`Error deleting invoice: ${error.response.data}`
            })

            handleToast({
              type:"ERROR",
              message:`Status code: ${error.response.status}`
            })
          } else if (error.request) {
            handleToast({
              type:"ERROR",
              message:"No response received from the server."
            })
          } else {
            handleToast({
              type:"ERROR",
              message:`Error setting up request: ${error.message}`
            })
          }
        });
    } catch (error) {
      handleToast({
        type:"ERROR",
        message:"An unexpected error occurred."
      })
    }
  };

  const handleRowClick = (invoice) => {
    onEdit(invoice);
  };

  const handleCellClick = (invoice, cellKey) => {
    onEdit(invoice);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedStatus("All");
    setSelectedVendor("");
    setDateRange([]);
    setCurrentPage(1);
    setSortOrder({ column: "", direction: "asc" });
  };

  const getSortIcon = (column) => {
    if (sortOrder.column === column) {
      return (
        <svg
          width="8"
          height="10"
          viewBox="0 0 8 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M0 6L4 10L8 6H0Z" fill="white" />
          <path d="M0 4L4 0L8 4H0Z" fill="white" />
        </svg>
      );
    }
    return null;
  };

  const renderPagination = () => {
    const paginationItems = [];

    if (totalPages <= 1) return null;

    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      paginationItems.push(
        <button
          key={1}
          className="pgbtn"
          onClick={() => setCurrentPage(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        paginationItems.push(
          <span key="ellipsis1" className="pagination-ellipsis">
            ...
          </span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      paginationItems.push(
        <button
          key={i}
          className={currentPage === i ? "active-pgbtn" : "pgbtn"}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        paginationItems.push(
          <span key="ellipsis2" className="pagination-ellipsis">
            ...
          </span>
        );
      }
      paginationItems.push(
        <button
          key={totalPages}
          className="pgbtn"
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    return (
      <div className="pagination">
        {paginationItems}
      </div>
    );
  };

  if (loading && searchQuery === debouncedSearchQuery) {
    return (
      <LoadingOverlay loading={loading} loader={loader} />
    );
  }

  return (
    <div>
      <ToastContainer />

      <div className="d-flex align-items-center mb-3" style={{ gap: "10px" }}>
        {}
        <div className="expense-searchcontainerstart d-flex">
          <div className="search-container">
            <input
              type="text"
              style={{
                width: "300px",
                height: "45px",
                backgroundColor: "#fff",
                border: "1px solid #DBDBDB",
                boxShadow: "0px 0px 10px rgba(187, 187, 187, 0.25)",
              }}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1); 
              }}
              placeholder="Search"
            />
            <svg
              className="search-container-icon"
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.5416 19.2497C15.3511 19.2497 19.2499 15.3508 19.2499 10.5413C19.2499 5.73186 15.3511 1.83301 10.5416 1.83301C5.73211 1.83301 1.83325 5.73186 1.83325 10.5413C1.83325 15.3508 5.73211 19.2497 10.5416 19.2497Z"
                stroke="#292D32"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M20.1666 20.1663L18.3333 18.333"
                stroke="#292D32"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

        </div>

        {}
        <div className="d-flex align-items-center">
          <div
            className="filterbutton"
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "60px",
              padding: "15px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <button
              className="btn dropdown"
              type="button"
              id="dropdownMenuButton"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              style={{
                fontSize: "14px",
                display: "flex",
                marginLeft: "6px",
                alignItems: "center",
                justifyContent: "center",
                padding: "0",
                border: "none",
                background: "transparent",
                outline: "none",
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.0707 1.83301H11.2291C11.9074 1.83301 12.4665 2.39218 12.4665 3.07051V4.42716C12.4665 4.92216 12.1549 5.53635 11.8524 5.84801L9.19408 8.19468C8.82741 8.50634 8.57987 9.1205 8.57987 9.6155V12.2739C8.57987 12.6405 8.33239 13.1355 8.02072 13.328L7.15905 13.8872C6.35238 14.3822 5.24319 13.823 5.24319 12.833V9.5605C5.24319 9.12967 4.99571 8.57052 4.74821 8.25885L2.40155 5.78384C2.08988 5.47217 1.8424 4.92217 1.8424 4.54633V3.12551C1.83323 2.39218 2.39236 1.83301 3.0707 1.83301Z"
                  stroke="#000000"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M1.8335 11.0004V13.7504C1.8335 18.3337 3.66683 20.1671 8.25016 20.1671H13.7502C18.3335 20.1671 20.1668 18.3337 20.1668 13.7504V8.25039C20.1668 5.39039 19.4518 3.59371 17.7926 2.65871C17.3251 2.39288 16.3902 2.19121 15.5377 2.05371"
                  stroke="#000000"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11.9165 11.917H16.4998"
                  stroke="#000000"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.0835 15.583H16.5002"
                  stroke="#000000"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              &nbsp;
            </button>

            <ul
              className="dropdown-menu p-3"
              aria-labelledby="dropdownMenuButton"
              style={{
                width: "290px",
                justifyContent: "center",
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1,
              }}
            >
              <li>
                <div className="mb-2">
                  <select
                    id="selectStatus"
                    className="form-select"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    style={{ borderRadius: "60px", padding: "auto" }}
                  >
                    <option value="All">All Status</option>
                    {["unpaid", "partially_paid", "paid",].map((status) => (
                      <option key={status} value={status}>
                        {status === "partially_paid"
                          ? "Partially Paid"
                          : status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </li>
              <li>
                <div className="mb-2">
                  <select
                    id="selectVendor"
                    className="form-select"
                    value={selectedVendor}
                    onChange={(e) => setSelectedVendor(e.target.value)}
                    style={{ borderRadius: "60px", padding: "auto" }}
                  >
                    <option value="">All Vendors</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </option>
                    ))}
                  </select>
                </div>
              </li>
              <li className="text-center mt-2">
                <button
                  className="btn btn-secondary me-2"
                  style={{ width: "75px", height: "40px", fontSize: "12px" }}
                  onClick={clearFilters}
                >
                  Clear
                </button>
              </li>
            </ul>
          </div>
        </div>

        {}
        <div>
          <DatePicker.RangePicker
            style={{ borderRadius: "60px", height: "40px", width: "300px" }}
            onChange={(dates) => setDateRange(dates || [])} 
            format="YYYY-MM-DD"
            value={dateRange}
            placeholder={['Start Date', 'End Date']}
          />
        </div>
      </div>

      {}

      {}
      <table
        className="data-table custom-table"
        style={{
          borderRadius: "20px 20px 0 0",
          border: "1px solid #EBEAF2",
          borderCollapse: "separate",
          borderSpacing: 0,
          overflow: "hidden",
        }}
      >
        <thead className="table-header">
          <tr>
            {[
              "date",
              "vendor_department_name",
              "amount",
              "remaining_amount",
              "payment_method",
              "status",
              "actions",
            ].map((header, index) => (
              <th
                key={header}
                onClick={() => header !== "actions" && handleSort(header)}
                style={{
                  paddingLeft: index === 0 ? "30px" : "0",
                  cursor: header !== "actions" ? "pointer" : "default",
                  backgroundColor: "#282e26",
                  color: "#ffffff",
                  textAlign: "center",
                  alignContent: "center",
                }}
              >
                {header
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (char) => char.toUpperCase())}{" "}
                {header !== "actions" && getSortIcon(header)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="table-body">
          {currentInvoices.length !== 0 ? (
            currentInvoices.map((invoice) => (
              <tr key={invoice.id} onClick={() => handleRowClick(invoice)}>
                <td onClick={() => handleCellClick(invoice, "date")}>
                  {formatDate(invoice.date)}
                </td>
                <td
                  onClick={() =>
                    handleCellClick(invoice, "vendor_department_name")
                  }
                >
                  {invoice.vendor_department_name}
                </td>
                <td onClick={() => handleCellClick(invoice, "amount")}>
                  {invoice.amount}
                </td>
                <td
                  onClick={() => handleCellClick(invoice, "remaining_amount")}
                >
                  {invoice.remaining_amount}
                </td>
                <td onClick={() => handleCellClick(invoice, "payment_method")}>
                  <span style={getPaymentMethodStyle(invoice.pay_method)}>
                    {invoice.pay_method &&
                      invoice.pay_method
                        .split("_") 
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() +
                            word.slice(1).toLowerCase()
                        ) 
                        .join(" ")}
                  </span>
                </td>
                <td style={{ textAlign: "center", verticalAlign: "middle" }} onClick={() => handleCellClick(invoice, "status")}>
                  <span style={getStatusStyle(invoice.status)}>
                    {invoice.status &&
                      invoice.status
                        .split("_") 
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() +
                            word.slice(1).toLowerCase()
                        ) 
                        .join(" ")}
                  </span>
                </td>

                <td>
                  <div className="dropdown">
                    <button
                      className="btn btn-sm btn-outline-secondary dropdown-vertical"
                      type="button"
                      id={`actionDropdown-${invoice.id}`}
                      data-bs-toggle="dropdown"
                    >
                      <i
                        className="bi bi-three-dots"
                        style={{ transform: "rotate(90deg)", display: "block" }}
                      ></i>
                    </button>
                    <ul
                      className="dropdown-menu"
                      aria-labelledby={`actionDropdown-${invoice.id}`}
                    >
                      <li>
                        <div
                          className="dropdown-Delete"
                          onClick={() => handleDelete(invoice.id)}
                        >
                          Delete
                        </div>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan="7"
                style={{
                  textAlign: "center",
                  
                  fontWeight: "600",
                  fontSize: "24px",
                  background:
                    "linear-gradient(180deg, rgba(255, 255, 255, 0.1) -92.86%, #4A5546 71.43%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                No data found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {renderPagination()}
    </div>
  );
};

PayInvoiceTable.propTypes = {
  onEdit: PropTypes.func,
  reloadData: PropTypes.bool
};

const paymentMethodStyles = {
  bank: { backgroundColor: "" },
  "Business Cash": { backgroundColor: "" },
  "Credit Card": { backgroundColor: "" },
  cash: { backgroundColor: "" },
  cheque: { backgroundColor: "" },
  pay_later: { backgroundColor: "" },
  pay_now: { backgroundColor: "" },
  credit_invoice: { backgroundColor: "" },
};

const getPaymentMethodStyle = (method) => ({
  ...(paymentMethodStyles[method] || { backgroundColor: "#6c757d" }),
  borderRadius: "5px",
  padding: "10px 20px",
  color: "#000",
});

const statusStyles = {
  draft: {
    backgroundColor: "#D1D1EF",
    color: "#fff",
    borderRadius: "5px",
    padding: "5px 10px",
    width: "130px", 
    height: "30px", 
    fontSize: "14px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  unpaid: {
    backgroundColor: "#ff2024",
    color: "#fff",
    borderRadius: "5px",
    padding: "5px 10px",
    width: "130px", 
    height: "30px", 
    fontSize: "14px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  paid: {
    backgroundColor: "#198f51",
    color: "#fff",
    borderRadius: "5px",
    padding: "5px 10px",
    width: "130px", 
    height: "30px", 
    fontSize: "14px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  partially_paid: {
    backgroundColor: "#0c8ce9",
    color: "#fff",
    borderRadius: "5px",
    padding: "5px 10px",
    width: "130px", 
    height: "30px", 
    fontSize: "14px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};

const getStatusStyle = (status) => ({
  ...(statusStyles[status] || { backgroundColor: "#198f51" }),
  display: "inline-flex", 
  justifyContent: "center", 
  alignItems: "center",
  width: "130px", 
  maxWidth:"100%",
  height: "30px", 
  borderRadius: "5px",
  padding: "5px 10px",
  color: "#fff",
  fontSize: "14px",
  textAlign: "center",
});

export default PayInvoiceTable;